import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Plus, 
  Search, 
  Copy, 
  Download, 
  Trash2, 
  Eye, 
  FileText, 
  User, 
  Calendar,
  Mail,
  CheckCircle,
  Clock,
  AlertTriangle,
  BarChart3,
  Link,
  Settings
} from 'lucide-react';
import { SignatureService } from '@/services/signatureService';
import { SignatureDocument, CreateSignatureDocumentRequest, SignatureStats } from '@/types/signature';
import { useToast } from '@/hooks/use-toast';
import { formatDate, generateSafeFileName } from '@/utils/signatureUtils';

export const SignatureDashboard: React.FC = () => {
  const { toast } = useToast();
  const [documents, setDocuments] = useState<SignatureDocument[]>([]);
  const [stats, setStats] = useState<SignatureStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);

  const [formData, setFormData] = useState<CreateSignatureDocumentRequest>({
    titulo: '',
    descricao: '',
    requer_confirmacao_email: true,
    data_expiracao: '',
    metadata: {}
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [docs, statsData] = await Promise.all([
        SignatureService.getUserDocuments(),
        SignatureService.getStats()
      ]);
      setDocuments(docs);
      setStats(statsData);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar dados. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDocument = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Upload do arquivo se fornecido
      let url_pdf_original = formData.url_pdf_original;
      if (uploadFile) {
        const fileName = generateSafeFileName(uploadFile.name);
        
        // Validar tamanho do arquivo (máximo 10MB)
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (uploadFile.size > maxSize) {
          toast({
            title: "Arquivo muito grande",
            description: "O arquivo deve ter no máximo 10MB.",
            variant: "destructive"
          });
          return;
        }

        try {
          const response = await fetch(`${window.location.origin}/api/documents/upload-pdf`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              fileName,
              file: await fileToBase64(uploadFile)
            })
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Erro ao fazer upload do arquivo');
          }

          const uploadData = await response.json();
          url_pdf_original = uploadData.data.url;
        } catch (error) {
          console.error('Erro no upload:', error);
          toast({
            title: "Erro no upload",
            description: error instanceof Error ? error.message : "Erro ao fazer upload do arquivo",
            variant: "destructive"
          });
          return;
        }
      }

      // Preparar dados para envio, removendo campos vazios
      const documentData = {
        ...formData,
        url_pdf_original
      };

      // Remover data_expiracao se estiver vazia
      if (!documentData.data_expiracao || documentData.data_expiracao.trim() === '') {
        delete documentData.data_expiracao;
      }

      const newDocument = await SignatureService.createDocument(documentData);

      setDocuments(prev => [newDocument, ...prev]);
      setIsCreateDialogOpen(false);
      resetForm();
      
      toast({
        title: "Documento criado com sucesso!",
        description: "O link de assinatura foi gerado e está pronto para envio.",
        variant: "default"
      });

      // Recarregar estatísticas
      const newStats = await SignatureService.getStats();
      setStats(newStats);

    } catch (error) {
      console.error('Erro ao criar documento:', error);
      toast({
        title: "Erro",
        description: "Erro ao criar documento. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDocument = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir este documento?')) return;

    try {
      await SignatureService.deleteDocument(id);
      setDocuments(prev => prev.filter(doc => doc.id !== id));
      
      toast({
        title: "Documento excluído com sucesso!",
        description: "O documento e suas assinaturas foram removidos.",
        variant: "default"
      });

      // Recarregar estatísticas
      const newStats = await SignatureService.getStats();
      setStats(newStats);

    } catch (error) {
      console.error('Erro ao excluir documento:', error);
      toast({
        title: "Erro",
        description: "Erro ao excluir documento. Tente novamente.",
        variant: "destructive"
      });
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Link copiado!",
      description: "O link foi copiado para a área de transferência.",
      variant: "default"
    });
  };

  const resetForm = () => {
    setFormData({
      titulo: '',
      descricao: '',
      requer_confirmacao_email: true,
      data_expiracao: '',
      metadata: {}
    });
    setUploadFile(null);
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  const filteredDocuments = documents.filter(doc => {
    const matchesSearch = doc.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         doc.descricao?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || doc.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pendente':
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />Pendente</Badge>;
      case 'assinado':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Assinado</Badge>;
      case 'expirado':
        return <Badge variant="destructive"><AlertTriangle className="w-3 h-3 mr-1" />Expirado</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading && documents.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p>Carregando documentos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Estatísticas */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center">
                <BarChart3 className="h-8 w-8 text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total</p>
                  <p className="text-2xl font-bold">{stats.total_documentos}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center">
                <Clock className="h-8 w-8 text-yellow-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Pendentes</p>
                  <p className="text-2xl font-bold">{stats.documentos_pendentes}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center">
                <CheckCircle className="h-8 w-8 text-green-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Assinados</p>
                  <p className="text-2xl font-bold">{stats.documentos_assinados}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center">
                <AlertTriangle className="h-8 w-8 text-red-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Expirados</p>
                  <p className="text-2xl font-bold">{stats.documentos_expirados}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Cabeçalho */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold">Assinaturas Eletrônicas</h2>
          <p className="text-gray-600">Gerencie documentos para assinatura eletrônica</p>
        </div>
        
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              Novo Documento
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Criar Documento para Assinatura</DialogTitle>
              <DialogDescription>
                Crie um novo documento para assinatura eletrônica. Você pode fazer upload de um PDF ou usar um modelo padrão.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleCreateDocument} className="space-y-4">
              <div>
                <Label htmlFor="titulo">Título do Documento *</Label>
                <Input
                  id="titulo"
                  value={formData.titulo}
                  onChange={(e) => setFormData(prev => ({ ...prev, titulo: e.target.value }))}
                  placeholder="Ex: Contrato de Prestação de Serviços"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="descricao">Descrição</Label>
                <Textarea
                  id="descricao"
                  value={formData.descricao}
                  onChange={(e) => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
                  placeholder="Descrição detalhada do documento..."
                  rows={3}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="data_expiracao">Data de Expiração</Label>
                  <Input
                    id="data_expiracao"
                    type="datetime-local"
                    value={formData.data_expiracao}
                    onChange={(e) => setFormData(prev => ({ ...prev, data_expiracao: e.target.value }))}
                  />
                </div>
                
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="requer_confirmacao_email"
                    checked={formData.requer_confirmacao_email}
                    onCheckedChange={(checked) => setFormData(prev => ({ 
                      ...prev, 
                      requer_confirmacao_email: checked as boolean 
                    }))}
                  />
                  <Label htmlFor="requer_confirmacao_email">Requer confirmação por email</Label>
                </div>
              </div>
              
              <div>
                <Label htmlFor="upload">Upload do PDF (opcional)</Label>
                <Input
                  id="upload"
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Se não fornecer um PDF, o assinante poderá escolher um modelo padrão
                </p>
              </div>
              
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Criando...' : 'Criar Documento'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Input
            placeholder="Buscar documentos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-sm"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="Filtrar por status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            <SelectItem value="pendente">Pendentes</SelectItem>
            <SelectItem value="assinado">Assinados</SelectItem>
            <SelectItem value="expirado">Expirados</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Lista de Documentos */}
      <div className="grid gap-4">
        {filteredDocuments.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum documento encontrado</h3>
              <p className="text-gray-600">
                {documents.length === 0 
                  ? "Crie seu primeiro documento para assinatura eletrônica."
                  : "Nenhum documento corresponde aos filtros aplicados."
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredDocuments.map((doc) => (
            <Card key={doc.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-lg font-semibold">{doc.titulo}</h3>
                      {getStatusBadge(doc.status)}
                    </div>
                    
                    {doc.descricao && (
                      <p className="text-gray-600 mb-3">{doc.descricao}</p>
                    )}
                    
                    <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        Criado em {formatDate(doc.data_criacao)}
                      </div>
                      
                      {doc.data_expiracao && (
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          Expira em {formatDate(doc.data_expiracao)}
                        </div>
                      )}
                      
                      {doc.requer_confirmacao_email && (
                        <div className="flex items-center">
                          <Mail className="w-4 h-4 mr-1" />
                          Requer confirmação
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(doc.link_assinatura)}
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      Copiar Link
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(doc.link_assinatura, '_blank')}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Visualizar
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(`/verificar/${doc.token_publico}`, '_blank')}
                    >
                      <Link className="w-4 h-4 mr-1" />
                      Verificar
                    </Button>
                    
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteDocument(doc.id)}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Excluir
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};
